import React from 'react';

/**
 * Composite page-skeleton primitives used across the app for loading states.
 *
 * Design language:
 *  - Warm bone background (#F5EFE3) on white shell so the placeholder visually
 *    rhymes with the cream layout when the real component pops in.
 *  - Subtle pulse animation via tailwind `animate-pulse`. Light on CPU.
 *  - Rounded corners + spacing match the live components, so the loading
 *    state doesn't cause layout shift when data arrives.
 *
 * Atomic primitive:        <SkBar />          arbitrary-shape bar
 * List/Table loading:      <SkeletonTable />  N rows × N cols inside card shell
 * Card-grid loading:       <SkeletonCards />  N tile placeholders in a grid
 * Detail-page hero:        <SkeletonHero />   avatar + title + 4 KPI bricks
 * Single KPI tile:         <SkeletonStat />   small label + value placeholder
 *
 * All accept `testid` so automation can assert "loading" → "loaded".
 */

export function SkBar({ className = '', style, testid }) {
  return (
    <div
      data-testid={testid}
      className={`skeleton rounded ${className}`}
      style={style}
    />
  );
}

export function SkeletonTable({ rows = 6, cols = 4, withHeader = true, testid = 'skeleton-table' }) {
  return (
    <div className="rounded-2xl border border-[#EDE6D8] bg-white p-4" data-testid={testid}>
      {withHeader && (
        <div
          className="grid gap-3 mb-4 pb-3 border-b border-[#EDE6D8]"
          style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
        >
          {Array.from({ length: cols }).map((_, c) => (
            <SkBar key={c} className="h-3 w-2/3" />
          ))}
        </div>
      )}
      <div className="space-y-3">
        {Array.from({ length: rows }).map((_, r) => (
          <div
            key={r}
            className="grid gap-3 items-center"
            style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
          >
            {Array.from({ length: cols }).map((_, c) => (
              <SkBar
                key={c}
                className="h-4"
                style={{ width: `${60 + ((r + c) % 4) * 9}%` }}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function SkeletonCards({ count = 6, cols = 3, testid = 'skeleton-cards' }) {
  return (
    <div
      data-testid={testid}
      className="grid gap-4"
      style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
    >
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-2xl border border-[#EDE6D8] bg-white p-5">
          <SkBar className="h-3 w-1/3 mb-3" />
          <SkBar className="h-7 w-2/3 mb-4" />
          <SkBar className="h-3 w-full mb-2" />
          <SkBar className="h-3 w-4/5" />
        </div>
      ))}
    </div>
  );
}

export function SkeletonHero({ testid = 'skeleton-hero' }) {
  return (
    <div className="rounded-2xl border border-[#EDE6D8] bg-white p-6" data-testid={testid}>
      <div className="flex items-start gap-4">
        <SkBar className="h-16 w-16 rounded-full shrink-0" />
        <div className="flex-1">
          <SkBar className="h-6 w-1/3 mb-2" />
          <SkBar className="h-3 w-1/4 mb-1" />
          <SkBar className="h-3 w-1/2" />
        </div>
        <div className="hidden md:flex gap-2">
          <SkBar className="h-9 w-20 rounded-xl" />
          <SkBar className="h-9 w-20 rounded-xl" />
        </div>
      </div>
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        {[0,1,2,3].map(i => (
          <div key={i} className="rounded-xl border border-[#EDE6D8] p-4">
            <SkBar className="h-3 w-1/2 mb-2" />
            <SkBar className="h-6 w-3/4" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function SkeletonStat({ testid }) {
  return (
    <div className="rounded-2xl border border-[#EDE6D8] bg-white p-5" data-testid={testid}>
      <SkBar className="h-3 w-1/3 mb-3" />
      <SkBar className="h-8 w-2/3" />
    </div>
  );
}

export function SkeletonPage({ rows = 8, cols = 5, testid = 'skeleton-page' }) {
  // Generic top-level "loading the entire page" — title bar + filter row + table.
  return (
    <div className="space-y-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <SkBar className="h-7 w-44" />
        <SkBar className="h-9 w-36 rounded-xl" />
      </div>
      <div className="rounded-2xl border border-[#EDE6D8] bg-white p-4">
        <div className="flex gap-3 mb-4">
          <SkBar className="h-9 w-1/3 rounded-xl" />
          <SkBar className="h-9 w-32 rounded-xl" />
          <SkBar className="h-9 w-32 rounded-xl" />
        </div>
        <SkeletonTable rows={rows} cols={cols} />
      </div>
    </div>
  );
}

export default SkBar;
